import snapshot from '../data/content-fallback.generated.json'

/* =========================================================================
   CONTENT FALLBACK

   A copy of the site's real content, taken from the live API at build time by
   scripts/build-content-fallback.mjs and shipped inside the bundle.

   It exists for one situation: the database is unreachable, every content
   endpoint returns 500, and the page would otherwise have nothing to show. The
   answer to that is not an empty section and it is certainly not the invented
   demo rows the homepage used to fall back on — it is the content that was
   really there when the site was last deployed.

   Two things to be clear about, because a silent fallback can hide a fault:

     - it is only ever used when a fetch actually fails. A successful response,
       even an empty one, wins. The database is the truth; this is the memory
       of it.
     - it goes stale by design. It is refreshed on every build, so the way to
       update it is to redeploy — the same action that publishes any other
       content change.

   `usedFallback` is exposed so a caller can tell the difference between "this
   is live" and "this is the last thing we knew", which is what makes an outage
   findable rather than invisible.
   ========================================================================= */

export const fallbackGeneratedAt = snapshot.generatedAt || null

export const fallbackContent = {
  projects: snapshot.projects || [],
  reviews: snapshot.reviews || [],
  reviewVideos: snapshot.reviewVideos || [],
  internships: snapshot.internships || [],
  team: snapshot.team || [],
  settings: snapshot.settings || null,
}

/**
 * Fetch content, falling back to the baked snapshot when the request fails or
 * the server answers with an error.
 *
 * @param {string} url      full URL to fetch
 * @param {Function} pick   pull the payload out of the parsed response
 * @param {string} key      which slice of the snapshot to fall back to
 * @returns {{data: any, usedFallback: boolean}}
 */
export async function fetchWithFallback(url, pick, key) {
  try {
    const res = await fetch(url, { headers: { Accept: 'application/json' } })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const value = pick(await res.json())
    /* An empty-but-successful answer is a real answer: the operator may have
       deleted everything on purpose, and quietly resurrecting old rows would
       make that impossible to do. */
    if (value !== null && value !== undefined) {
      return { data: value, usedFallback: false }
    }
    throw new Error('empty payload')
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn(`[content] ${key}: live fetch failed, using the baked snapshot —`, err.message)
    }
    return { data: fallbackContent[key], usedFallback: true }
  }
}

export default fallbackContent
